// src/components/common/ConfirmPhraseField.js
//
// The "Type DELETE to confirm" field, extracted from ConfirmDialog so the three
// confirm shells stop hand-rolling their own copy of it. ConfirmDialog,
// ConfirmDialogInline and BulkActionProgressModal each had a near-identical
// block; that is precisely why they drifted on case-sensitivity (the bulk one
// compared exact-case long after the others stopped).
//
// Behaviour notes that are load-bearing, not decoration:
//  - The phrase is a select-all CHIP under the label (RD-UI-005), not text in
//    the label sentence; no copy button / tap-to-fill by design.
//  - `typedConfirmPhraseInputProps` marks the field as NOT an OTP box, or
//    Safari offers a verification code whenever nearby copy mentions 2FA.
//  - The readOnly/release dance is the coarse-pointer autofill guard; all three
//    handlers must be bound, because onTouchStart is the only one that clears
//    before focus on iOS.
//  - Enter is handled by the parent's document-level key binding, NOT here, so
//    the keyboard and click paths share one gate. Do not add an onKeyDown that
//    confirms — that is the divergence ConfirmDialog.enterGuard.test.js pins.

import { useId } from 'react';
import { useIntl } from 'react-intl';
import { foundation } from '../../ui/tokens';
import {
  typedConfirmInputSection,
  typedConfirmInputLabel,
  typedConfirmPhraseChip,
  typedConfirmInputField,
} from '../../ui/confirmDialogStyles';
import {
  typedConfirmPhraseInputProps,
  useTypedConfirmPhraseInput,
} from './typedConfirmPhraseInput';

const { color, fontSize, fontFamily } = foundation;

export default function ConfirmPhraseField({
  phrase,
  value,
  onChange,
  caseInsensitive = false,
  testId = 'confirm-dialog-require-input',
  disabled = false,
  /** Override the label TEXT only; the phrase chip always renders under it. */
  labelNode,
}) {
  const intl = useIntl();
  const inputId = useId();
  const phraseId = `${inputId}-phrase`;
  const { blockAutofill, releaseAutofillBlock } = useTypedConfirmPhraseInput();

  return (
    <div style={typedConfirmInputSection}>
      <label htmlFor={inputId} style={typedConfirmInputLabel}>
        {labelNode || intl.formatMessage({
          id: 'common.typeToConfirm.chipLabel',
          defaultMessage: 'To confirm, type:',
        })}
      </label>
      <span id={phraseId} data-testid={`${testId}-phrase`} style={typedConfirmPhraseChip}>
        {phrase}
      </span>
      <input
        {...typedConfirmPhraseInputProps}
        id={inputId}
        aria-describedby={phraseId}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        enterKeyHint="done"
        readOnly={blockAutofill && !disabled}
        onFocus={releaseAutofillBlock}
        onMouseDown={releaseAutofillBlock}
        onTouchStart={releaseAutofillBlock}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={typedConfirmInputField}
        disabled={disabled}
        data-testid={testId}
      />
      {caseInsensitive ? (
        <span style={{
          fontSize: fontSize.xs,
          color: color.neutral.textSecondary,
          fontFamily: fontFamily.sans,
        }}
        >
          {intl.formatMessage({
            id: 'common.typeToConfirm.caseInsensitiveHint',
            defaultMessage: "Capital letters don't matter.",
          })}
        </span>
      ) : null}
    </div>
  );
}
